import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Play } from 'lucide-react';
import Navigation from '../components/shared/Navigation';
import Footer from '../components/shared/Footer';
import UnitCard from '../components/shared/UnitCard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function HomePage() {
  const [units, setUnits] = useState([]);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState('exterior'); // 'exterior' or 'interior'

  useEffect(() => {
    fetch(`${API_URL}/api/units`)
      .then(res => res.json())
      .then(data => setUnits(data.slice(0, 4))) // Show 4 units on homepage
      .catch(err => console.error(err));
  }, []);

  return (
    <div>
      <Navigation />
      
      {/* Hero Section - Sophisticated Dark Design */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_ellis-discover/artifacts/mfvba2pc_PHOTO-2025-08-19-10-33-41.jpeg"
            alt="Ellis Row Interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/50"></div>
        </div>
        
        <div className="container py-32 relative z-10">
          <div className="max-w-3xl">
            {/* Subtle Brand Attribution */}
            <p className="text-sm text-white/60 mb-8 tracking-wider uppercase">A Mods Enterprises Development</p>
            
            <h1 className="text-6xl sm:text-7xl lg:text-8xl font-[var(--font-display)] tracking-[-0.03em] mb-8 leading-[0.95] text-white">
              Where Your Future<br />
              Home Begins
            </h1>
            
            <p className="text-xl md:text-2xl text-white/80 mb-12 leading-relaxed max-w-2xl font-light">
              Distinctive peaked-roof townhomes in central Iowa. Modern architecture meets thoughtful design.
            </p>

            {/* Timeline Callout - More Minimal */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 mb-12 inline-block">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-white rounded-full"></div>
                <div>
                  <p className="text-base font-medium text-white">Breaking Ground Summer 2026</p>
                  <p className="text-sm text-white/60 mt-1">4 rentals · 3 for sale · Completion Fall/Winter 2026</p>
                </div>
              </div>
            </div>

            {/* CTAs - Simplified */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/units">
                <Button
                  data-testid="hero-view-units-button"
                  size="lg"
                  className="bg-white text-black hover:bg-white/90 rounded-sm text-sm px-8 py-6 w-full sm:w-auto font-medium tracking-wide uppercase transition-all duration-200"
                >
                  View Units
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-transparent border border-white/30 text-white hover:bg-white/10 hover:border-white/50 rounded-sm text-sm px-8 py-6 w-full sm:w-auto font-medium tracking-wide uppercase transition-all duration-200"
                >
                  Schedule Tour
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="flex flex-col items-center text-white/60 animate-bounce">
            <span className="text-xs mb-2 tracking-wider uppercase">Scroll</span>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="bg-[#121416] text-[#FBF8F2] py-8">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-[var(--font-display)] text-[#A06A3B]">4</div>
              <div className="text-sm text-[#E9E3DA] mt-1">Rental Units</div>
            </div>
            <div>
              <div className="text-3xl font-[var(--font-display)] text-[#A06A3B]">3</div>
              <div className="text-sm text-[#E9E3DA] mt-1">For Sale</div>
            </div>
            <div>
              <div className="text-3xl font-[var(--font-display)] text-[#A06A3B]">9ft</div>
              <div className="text-sm text-[#E9E3DA] mt-1">Ceilings</div>
            </div>
            <div>
              <div className="text-3xl font-[var(--font-display)] text-[#A06A3B]">2026</div>
              <div className="text-sm text-[#E9E3DA] mt-1">Completion</div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Features Highlight */}
      <section className="section-spacing bg-gradient-to-b from-white to-muted/30">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-[var(--font-display)] tracking-[-0.01em] mb-4">
              Premium Features & Finishes
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Every detail thoughtfully designed for modern luxury living
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#A06A3B]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-8 h-8 text-[#A06A3B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
              </div>
              <h3 className="font-semibold mb-1">9-Foot Ceilings</h3>
              <p className="text-sm text-muted-foreground">Soaring heights</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#A06A3B]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-8 h-8 text-[#A06A3B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                </svg>
              </div>
              <h3 className="font-semibold mb-1">Floor-to-Ceiling Windows</h3>
              <p className="text-sm text-muted-foreground">Natural light</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#A06A3B]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-8 h-8 text-[#A06A3B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
              <h3 className="font-semibold mb-1">8-Foot Interior Doors</h3>
              <p className="text-sm text-muted-foreground">Grand entryways</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#A06A3B]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-8 h-8 text-[#A06A3B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="font-semibold mb-1">Aluminum Windows & Doors</h3>
              <p className="text-sm text-muted-foreground">Modern durability</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="section-spacing">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-[var(--font-display)] tracking-[-0.01em] mb-4">
              Architectural Excellence
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Explore the distinctive design and premium finishes that define Ellis Row
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              "https://customer-assets.emergentagent.com/job_ellis-discover/artifacts/brb3lph6_PHOTO-2025-08-18-10-42-38.jpeg",
              "https://customer-assets.emergentagent.com/job_ellis-discover/artifacts/mfvba2pc_PHOTO-2025-08-19-10-33-41.jpeg",
              "https://customer-assets.emergentagent.com/job_ellis-discover/artifacts/mtr9eg9c_PHOTO-2025-08-16-06-42-30.jpeg"
            ].map((img, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group overflow-hidden rounded-xl shadow-lg"
              >
                <img
                  src={img}
                  alt={`Ellis Row View ${idx + 1}`}
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Units */}
      <section className="section-spacing bg-muted/30">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-[var(--font-display)] tracking-[-0.01em] mb-4">
              Available Units
            </h2>
            <p className="text-lg text-muted-foreground">Browse our selection of rental and for-sale units</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {units.map((unit, idx) => (
              <UnitCard key={unit.id} unit={unit} delay={idx * 0.1} />
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/units">
              <Button variant="outline" size="lg" className="rounded-[var(--btn-radius)]">
                View All Units <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Neighborhood Section */}
      <section className="section-spacing">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-[var(--font-display)] tracking-[-0.01em] mb-6">
              This Is Where You Want To Be
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Ellis Row isn't just a place to live—it's the gateway to everything that makes this community exceptional. 
              From world-class entertainment to natural beauty, your new lifestyle starts here.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="h-full">
                <CardHeader>
                  <div className="w-12 h-12 bg-[#A06A3B]/10 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-[#A06A3B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <CardTitle className="text-xl">Cedar River Casino</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    World-class entertainment coming soon. Experience premier gaming, live concerts, and fine dining just minutes from your doorstep.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="h-full">
                <CardHeader>
                  <div className="w-12 h-12 bg-[#A06A3B]/10 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-[#A06A3B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
                    </svg>
                  </div>
                  <CardTitle className="text-xl">Riverside Trails</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Miles of scenic walking and biking trails along the river. Connect with nature while staying active in your own backyard.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Card className="h-full">
                <CardHeader>
                  <div className="w-12 h-12 bg-[#A06A3B]/10 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-[#A06A3B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <CardTitle className="text-xl">Downtown Access</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Just minutes from vibrant downtown. Easy access to work, dining, shopping, and cultural attractions while enjoying suburban tranquility.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <Card className="h-full">
                <CardHeader>
                  <div className="w-12 h-12 bg-[#A06A3B]/10 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-[#A06A3B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                  </div>
                  <CardTitle className="text-xl">Live Entertainment</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    The Amphitheater hosts world-class concerts and events. Experience unforgettable performances under the stars, all close to home.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              <Card className="h-full">
                <CardHeader>
                  <div className="w-12 h-12 bg-[#A06A3B]/10 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-[#A06A3B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                    </svg>
                  </div>
                  <CardTitle className="text-xl">Pickle Palace</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    State-of-the-art pickleball facility with courts, leagues, and social events. Join the fastest-growing sport in America.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
            >
              <Card className="h-full">
                <CardHeader>
                  <div className="w-12 h-12 bg-[#A06A3B]/10 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-[#A06A3B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <CardTitle className="text-xl">Riverfront Living</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Kayaking, fishing, and riverside parks at your fingertips. Embrace waterfront living with endless recreational opportunities.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Lifestyle CTA Section */}
      <section className="section-spacing bg-[#121416] text-[#FBF8F2] grain relative overflow-hidden">
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-[var(--font-display)] tracking-[-0.02em] mb-8">
                This Is My Home
              </h2>
              <p className="text-lg md:text-xl text-[#E9E3DA] mb-10 leading-relaxed max-w-2xl mx-auto">
                More than just a residence, Ellis Row is where modern luxury meets authentic community. 
                Where entertainment, nature, and convenience converge. Where your story begins.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/units">
                  <Button
                    size="lg"
                    className="bg-[#A06A3B] text-[#FBF8F2] hover:bg-[#6F4526] shadow-[0_14px_30px_rgba(160,106,59,0.25)] hover:shadow-[0_20px_40px_rgba(160,106,59,0.30)] rounded-[var(--btn-radius)] font-semibold transition-all duration-300 hover:-translate-y-0.5">
                    Find Your Home <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    size="lg"
                    variant="outline"
                    className="bg-transparent border-2 border-[#E9E3DA] text-[#E9E3DA] hover:bg-[#E9E3DA] hover:text-[#121416] rounded-[var(--btn-radius)] font-semibold transition-all duration-300 hover:-translate-y-0.5"
                  >
                    Schedule a Tour
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Video Dialog */}
      <Dialog open={videoDialogOpen} onOpenChange={setVideoDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Ellis Row Walkthrough</DialogTitle>
          </DialogHeader>
          <div className="aspect-video">
            <video
              data-testid="walkthrough-video-player"
              controls
              className="w-full h-full rounded-lg"
              poster="https://customer-assets.emergentagent.com/job_ellis-discover/artifacts/brb3lph6_PHOTO-2025-08-18-10-42-38.jpeg"
            >
              <source src="https://customer-assets.emergentagent.com/job_ellis-discover/artifacts/o7t0uag1_28900B4D-0B03-4731-BB93-023D81687DE9.mp4" type="video/mp4" />
            </video>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}